import React, { useState, useEffect } from 'react';

interface JournalExample {
  title: string;
  journal: string;
  impactFactor?: number;
  category: 'high' | 'medium' | 'low' | 'tool';
  year: string;
  image: string;
  link: string;
  analysisMethod?: string;
  toolLabel?: string; // ✅ 新增：工具/包的名字，用来显示在 banner 上
  keywords?: string[]; // ✅ 新增：多组学标签，如 ['RNA-seq', 'ATAC-seq', 'Metabolomics']
}

const ResearchDirections: React.FC = () => {
  const [selectedDirection, setSelectedDirection] = useState('transcriptomics');
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'high' | 'medium' | 'low' | 'trajectory' | 'tf-prediction' | 'multiomics-integration' | 'metabolomics' | 'geneomics' | 'cell-abundance' | 'cell-communication' | 'quality-control' | 'data-integration' | 'cell-perturbation' | 'cell-annotation' | 'spatial-transcriptomics' | 'metacells' | 'ML' | 'cancer' | 'other'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [toolSearch, setToolSearch] = useState('');
  const itemsPerPage = 12;

  const directions = [
    { id: 'transcriptomics', name: '转录组学', icon: 'ri-file-text-line' },
    { id: 'multiomics', name: '多组学整合', icon: 'ri-links-line' },
    { id: 'Biological-Intelligence', name: '数智科学', icon: 'ri-cpu-line' },
    { id: 'cell-annotation', name: '细胞注释', icon: 'ri-price-tag-3-line' },
    { id: 'analysis-tools', name: '分析工具', icon: 'ri-tools-line' }
  ];
  // 🔥 1. 只要组件渲染，这个小黑块就应该出现在右上角
  //    如果你在页面上根本看不到这个小块，说明这个组件没被用到
  const debugBadge = (
    <div className="fixed top-2 right-2 z-50 bg-black text-white text-xs px-2 py-1 rounded">
      DEBUG selectedDirection: {selectedDirection}
    </div>
  );
  const categories = selectedDirection === 'analysis-tools' ? [
    { id: 'all', name: '全部方法', color: 'bg-gray-100 text-gray-700' },
    { id: 'trajectory', name: '细胞轨迹', color: 'bg-blue-100 text-blue-700' },
    { id: 'tf-prediction', name: '转录因子', color: 'bg-green-100 text-green-700' },
    { id: 'multiomics-integration', name: '多组学整合', color: 'bg-purple-100 text-purple-700' },
    { id: 'metabolomics', name: '代谢组学', color: 'bg-orange-100 text-orange-700' },
    { id: 'geneomics', name: '基因组学', color: 'bg-stone-100 text-stone-700' },
    { id: 'cell-abundance', name: '细胞丰度', color: 'bg-teal-100 text-teal-700' },
    { id: 'cell-communication', name: '细胞通讯', color: 'bg-pink-100 text-pink-700' },
    { id: 'quality-control', name: '质控', color: 'bg-indigo-100 text-indigo-700' },
    { id: 'data-integration', name: '数据整合', color: 'bg-cyan-100 text-cyan-700' },
    { id: 'cell-perturbation', name: '细胞扰动', color: 'bg-red-100 text-red-700' },
    { id: 'cell-annotation', name: '细胞注释', color: 'bg-emerald-100 text-emerald-700' },
    { id: 'spatial-transcriptomics', name: '空间转录组', color: 'bg-violet-100 text-violet-700' },
    { id: 'metacells', name: 'Metacells', color: 'bg-amber-100 text-amber-700' },
    { id: 'ML', name: '机器学习', color: 'bg-slate-100 text-slate-700' },
    { id: 'cancer', name: '癌症', color: 'bg-rose-100 text-rose-700' },
    { id: 'other', name: '其它', color: 'bg-lime-100 text-lime-700' },
  ] : [
    { id: 'all', name: '全部', color: 'bg-gray-100 text-gray-700' },
    { id: 'high', name: 'IF ≥ 10', color: 'bg-red-100 text-red-700' },
    { id: 'medium', name: 'IF 5-8', color: 'bg-yellow-100 text-yellow-700' },
    { id: 'low', name: 'IF ≤ 3', color: 'bg-green-100 text-green-700' }
  ];

  const journalExamples: Record<string, JournalExample[]> = {
    transcriptomics: [
      {
        title: "Integrating spatial and single-cell transcriptomics to characterize the molecular and cellular architecture of the ischemic mouse brain",
        journal: "Science Translational Medicine",
        impactFactor: 17.1,
        category: 'high',
        year: '2024',
        image: 'https://readdy.ai/api/search-image?query=Spatial%20transcriptomics%20and%20single-cell%20RNA%20sequencing%20analysis%20of%20ischemic%20mouse%20brain%20tissue%2C%20scientific%20visualization%20showing%20brain%20cellular%20architecture%20with%20molecular%20mapping%2C%20colorful%20cell%20type%20identification%20overlaid%20on%20brain%20tissue%20sections%2C%20neural%20network%20visualization%20with%20gene%20expression%20patterns%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=spatial-scrna-ischemic-brain-v2&orientation=landscape',
        link: 'https://www.science.org/doi/10.1126/scitranslmed.adg1323?url_ver=Z39.88-2003'
      },
      {
        title: "Single-Cell RNA Sequencing Delineates Renal Anti-Fibrotic Mechanisms Mediated by TRPC6 Inhibition",
        journal: "Advanced Science",
        impactFactor: 14.3,
        category: 'high',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=Single-cell%20RNA%20sequencing%20analysis%20of%20kidney%20tissue%20showing%20renal%20anti-fibrotic%20mechanisms%20and%20TRPC6%20inhibition%20pathways%2C%20scientific%20visualization%20with%20kidney%20cellular%20structure%2C%20fibrosis%20reduction%20visualization%2C%20molecular%20pathway%20diagrams%20with%20colorful%20cell%20clusters%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=renal-antifibrotic-trpc6-v2&orientation=landscape',
        link: 'https://doi.org/10.1002/advs.202501175'
      },
      {
        title: "Aberrant intermediate alveolar epithelial cells promote pathogenic activation of lung fibroblasts in preclinical fibrosis models",
        journal: "Nature Communications",
        impactFactor: 14.7,
        category: 'high',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=Lung%20tissue%20showing%20aberrant%20intermediate%20alveolar%20epithelial%20cells%20and%20fibroblast%20activation%20in%20pulmonary%20fibrosis%2C%20scientific%20visualization%20of%20lung%20cellular%20architecture%2C%20pathogenic%20cell%20interactions%2C%20fibrosis%20progression%20with%20colorful%20cell%20type%20identification%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=lung-fibrosis-epithelial-v2&orientation=landscape',
        link: 'https://www.nature.com/articles/s41467-025-63735-1'
      },
      {
        title: "Single-cell transcriptomics reveals BMP4-BMPR2 signaling promotes radiation resistance in hematopoietic stem cells following injury",
        journal: "Nature Communications",
        impactFactor: 14.7,
        category: 'high',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=Hematopoietic%20stem%20cells%20with%20BMP4-BMPR2%20signaling%20pathway%20visualization%20showing%20radiation%20resistance%20mechanisms%2C%20scientific%20illustration%20of%20stem%20cell%20biology%2C%20bone%20marrow%20cellular%20environment%2C%20signaling%20cascade%20diagrams%20with%20colorful%20molecular%20interactions%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=hsc-bmp4-radiation-v2&orientation=landscape',
        link: 'https://www.nature.com/articles/s41467-025-60557-z'
      },
      {
        title: "Spatial-reprogramming derived GPNMB+ macrophages interact with COL6A3+ fibroblasts to enhance vascular fibrosis in glioblastoma",
        journal: "Genome Medicine",
        impactFactor: 12.3,
        category: 'high',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=Spatial%20transcriptomics%20analysis%20of%20glioblastoma%20showing%20GPNMB%20positive%20macrophages%20interacting%20with%20COL6A3%20positive%20fibroblasts%20in%20vascular%20fibrosis%2C%20scientific%20visualization%20of%20brain%20tumor%20microenvironment%2C%20cellular%20interactions%20in%20cancer%20progression%2C%20spatial%20gene%20expression%20patterns%20with%20colorful%20cell%20type%20mapping%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=glioblastoma-gpnmb-col6a3-v1&orientation=landscape',
        link: 'https://genomemedicine.biomedcentral.com/articles/10.1186/s11658-025-01553-2'
      },
      {
        title: "Mapping spatially resolved transcriptomes in human and mouse pulmonary fibrosis",
        journal: "Nature Genetics",
        impactFactor: 30.8,
        category: 'high',
        year: '2024',
        image: 'https://readdy.ai/api/search-image?query=Spatial%20transcriptomics%20mapping%20of%20human%20and%20mouse%20pulmonary%20fibrosis%20showing%20spatially%20resolved%20gene%20expression%20patterns%2C%20scientific%20visualization%20of%20lung%20tissue%20architecture%2C%20fibrotic%20regions%20with%20cellular%20heterogeneity%2C%20comparative%20analysis%20between%20species%20with%20colorful%20spatial%20gene%20expression%20overlays%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=spatial-pulmonary-fibrosis-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41588-024-01819-2'
      },
      {
        title: "Single-cell RNA sequencing identifies inherent abnormalities of adipose-derived stem cells from nonlesional sites of patients with localized scleroderma",
        journal: "Cell Mol Biol Lett",
        impactFactor: 10.2,
        category: 'high',
        year: '2024',
        image: 'https://readdy.ai/api/search-image?query=Adipose-derived%20stem%20cells%20from%20scleroderma%20patients%20showing%20cellular%20abnormalities%20and%20molecular%20defects%2C%20scientific%20visualization%20of%20stem%20cell%20biology%2C%20scleroderma%20pathology%2C%20adipose%20tissue%20cellular%20structure%2C%20single-cell%20RNA%20sequencing%20data%20with%20colorful%20cell%20type%20identification%20and%20disease%20mechanisms%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=adipose-stem-scleroderma-v2&orientation=landscape',
        link: 'https://doi.org/10.1186/s11658-024-00635-0'
      },
      {
        title: "Single-cell RNA sequencing reveals the CRTAC1+ population actively contributes to the pathogenesis of spinal ligament degeneration by SPP1+ macrophage",
        journal: "Aging Cell",
        impactFactor: 5.1,
        category: 'medium',
        year: '2024',
        image: 'https://readdy.ai/api/search-image?query=Spinal%20ligament%20tissue%20showing%20CRTAC1%20positive%20cell%20population%20and%20SPP1%20positive%20macrophages%20in%20ligament%20degeneration%2C%20scientific%20visualization%20of%20spine%20pathology%2C%20cellular%20interactions%20in%20aging%20tissue%2C%20inflammatory%20cell%20infiltration%20with%20molecular%20markers%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=spinal-crtac1-spp1-v2&orientation=landscape',
        link: 'https://onlinelibrary.wiley.com/doi/10.1111/acel.14320'
      },
      {
        title: "Single-cell RNA sequencing reveals BHLHE40 and NDRG1 as key regulatory molecules modulating the trophoblastic senescence in preeclampsia",
        journal: "Placenta",
        impactFactor: 2.8,
        category: 'low',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=Placental%20trophoblast%20cells%20showing%20BHLHE40%20and%20NDRG1%20regulatory%20molecules%20in%20preeclampsia%2C%20scientific%20visualization%20of%20trophoblastic%20senescence%20mechanisms%2C%20placental%20tissue%20cellular%20structure%2C%20pregnancy%20complications%20molecular%20pathways%20with%20gene%20expression%20patterns%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=placenta-bhlhe40-ndrg1-v2&orientation=landscape',
        link: 'https://doi.org/10.1016/j.placenta.2025.06.009'
      },
      {
        title: "Single-cell RNA sequencing reveals association of aberrant placental trophoblasts and FN1 reduction in late-onset fetal growth restriction",
        journal: "Placenta",
        impactFactor: 2.8,
        category: 'low',
        year: '2024',
        image: 'https://readdy.ai/api/search-image?query=Placental%20trophoblast%20cells%20with%20FN1%20gene%20expression%20reduction%20in%20fetal%20growth%20restriction%2C%20scientific%20visualization%20of%20aberrant%20placental%20development%2C%20maternal-fetal%20interface%20cellular%20interactions%2C%20developmental%20biology%20with%20colorful%20cell%20type%20mapping%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=placenta-fn1-fgr-v2&orientation=landscape',
        link: 'https://doi.org/10.1016/j.placenta.2023.12.022'
      },
      {
        "title": "Spatial fibroblast niches define Crohn’s fistulae",
        "journal": "Nature",
        "category": "high",
        "year": "2025",
        "image": "/images/research/09744.png",
        "link": "https://www.nature.com/articles/s41586-025-09744-y"
      }
    ],
    // 多组学
    multiomics: [
      {
        title: "Blood Biomarker-Based Predictive Indicator for Liver Metastasis in Alpha-Fetoprotein-Producing Gastric Cancer and Multi-Omics Tumor Microenvironment Insights",
        journal: "Advanced Science",
        impactFactor: 14.3,
        category: 'high',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=Multi-omics%20analysis%20of%20gastric%20cancer%20liver%20metastasis%20with%20blood%20biomarkers%20and%20tumor%20microenvironment%20visualization%2C%20scientific%20illustration%20showing%20cancer%20progression%2C%20alpha-fetoprotein%20molecular%20markers%2C%20integrated%20omics%20data%20networks%20with%20colorful%20pathway%20diagrams%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=gastric-liver-metastasis-v2&orientation=landscape',
        link: 'https://doi.org/10.1002/advs.202503499',
        keywords: ['胃癌肝转移', 'WES', '蛋白组', 'scRNA', "临床队列", "biomarkers"]  // ✅ 新增
      },
      {
        title: "Integrative analysis of lung adenocarcinoma across diverse ethnicities and exposures",
        journal: "Cancer Cell",
        impactFactor: 14.7,
        category: 'high',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=Integrative%20multi-omics%20analysis%20of%20lung%20adenocarcinoma%20showing%20diverse%20ethnic%20populations%20and%20environmental%20exposures%2C%20scientific%20visualization%20of%20cancer%20genomics%2C%20population%20diversity%20in%20cancer%20research%2C%20molecular%20heterogeneity%20with%20colorful%20data%20integration%20plots%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=lung-adenocarcinoma-diversity-v2&orientation=landscape',
        link: 'https://www.cell.com/cancer-cell/fulltext/S1535-6108(25)00317-4',
        keywords: ["肺腺癌", '基因组', "蛋白组", '转录组', "单细胞", "biomarkers"]  // ✅ 新增
      },
      {
        title: "Single-cell multi-omics and nursing follow-up prognostic modeling reveal SLFN4-mediated neutrophil dysregulation in traumatic brain injury",
        journal: "Front. Immunol",
        impactFactor: 7.9,
        category: 'medium',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=Single-cell%20multi-omics%20analysis%20of%20traumatic%20brain%20injury%20showing%20SLFN4-mediated%20neutrophil%20dysregulation%2C%20scientific%20visualization%20of%20brain%20immune%20response%2C%20neutrophil%20cellular%20dysfunction%2C%20prognostic%20modeling%20with%20colorful%20immune%20cell%20mapping%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=tbi-slfn4-neutrophil-v2&orientation=landscape',
        link: 'https://www.frontiersin.org/journals/immunology/articles/10.3389/fimmu.2025.1669800/full',
        keywords: ['创伤性脑损伤', 'scRNA', "bulk RNA", '蛋白组', "公共数据"]  // ✅ 新增
      }, {
        "title": "Blood pressure, plasma proteins, and cardiovascular diseases: a network Mendelian randomization and observational study",
        "journal": "European Heart Journal",
        "category": "high",
        "year": "2025",
        "image": "/images/research/1093.jpeg",
        "link": "https://doi.org/10.1093/eurheartj/ehaf725",
        "keywords": ["心脑血管疾病", "UKB", "临床队列", "蛋白组", "基因组", "孟德尔随机化"]
      }, {
        "title": "Multi-omic profiling reveals age-related immune dynamics in healthy adults",
        "journal": "Nature",
        "category": "high",
        "year": "2025",
        "image": "/images/research/09686-5.png",
        "link": "https://www.nature.com/articles/s41586-025-09686-5",
        "keywords": ["衰老", "免疫", "蛋白组", "scRNA"]
      }, {
        "title": "Plasma proteomics identify biomarkers and undulating changes of brain aging",
        "journal": "Nature Aging",
        "category": "high",
        "year": "2025",
        "image": "/images/research/00753.webp",
        "link": "https://doi.org/10.1038/s43587-024-00753-6",
        "keywords": ["衰老", "UKB", "蛋白组学", "孟德尔随机化", "基因组", "scRNA", "生物标志物", "公共数据"]
      }, {
        "title": "Single-cell multiomics analysis reveals CTCF as a key regulator of lung morphogenesis and progenitor maintenance",
        "journal": "Nature Communications",
        "category": "high",
        "year": "2025",
        "image": "/images/research/65757-1.png",
        "link": "https://www.nature.com/articles/s41467-025-65757-1#Sec12",
        "keywords": ["发育", "scATAC", "scRNA","CUT&Tag"]
      } ,{
        "title": "Spatially resolved multi-omics of human metabolic dysfunction-associated steatotic liver disease",
        "journal": "Nature Genetics",
        "category": "high",
        "year": "2025",
        "image": "/images/research/02407-8.png",
        "link": "https://www.nature.com/articles/s41588-025-02407-8",
        "keywords": ["代谢疾病", "空间转录组", "空间代谢组","空间蛋白组","scRNA","scVDJ-seq"]
      }
    ],
    // 数智科学
    'Biological-Intelligence': [
      {"title": "Digital twins for in vivo metabolic flux estimations inpatients with brain cancer",
        "journal": "Cell Metabolism",
        "category": "high",
        "year": "2025",
        "image": "/images/research/fx1_lrg.jpg",
        "link": "https://www.cell.com/cell-metabolism/fulltext/S1550-4131(25)00482-6"
      },
    {"title": "Active learning framework leveraging transcriptomics identifies modulators of disease phenotypes",
        "journal": "Science",
        "category": "high",
        "year": "2025",
        "image": "/images/research/science.adi8577-fa.jpg",
        "link": "https://www.science.org/doi/10.1126/science.adi8577"
      }
    ],
    // 细胞注释
    'cell-annotation': [
      {
        "title": "Timing and location dictate monocyte fate and their transition to tumor-associated macrophages",
        "journal": "Science Immunology",
        "category": "high",
        "year": "2024",
        "image": "/images/research/1126.png",
        "link": "https://www.science.org/doi/10.1126/sciimmunol.adk3981",
        "keywords": ["单核细胞", "巨噬细胞", "小鼠"]
      },
      {
        "title": "Recruited atypical Ly6G+ macrophages license alveolar regeneration after lung injury",
        "journal": "Science Immunology",
        "category": "high",
        "year": "2024",
        "image": "/images/research/1126.jpg",
        "link": "https://www.science.org/doi/10.1126/sciimmunol.ado1227",
        "keywords": ["巨噬细胞", "小鼠"]
      },
      {
        "title": "Single-Cell Analyses Inform Mechanisms of Myeloid-Targeted Therapies in Colon Cancer",
        "journal": "Cell",
        "category": "high",
        "year": "2020",
        "image": "/images/research/1016.jpg",
        "link": "https://www.cell.com/cell/fulltext/S0092-8674(20)30341-X",
        "keywords": ["髓系细胞", "人类"]
      }, {
        "title": "Single-cell profiling of vascular endothelial cells reveals progressive organ-specific vulnerabilities during obesity",
        "journal": "Nature Metabolism",
        "category": "high",
        "year": "2022",
        "image": "/images/research/42255.png",
        "link": "https://www.nature.com/articles/s42255-022-00689-9",
        "keywords": ["内皮细胞", "小鼠"]
      }, {
        "title": "An organotypic atlas of human vascular cells",
        "journal": "Nature Medicine",
        "category": "high",
        "year": "2024",
        "image": "/images/research/41591.png",
        "link": "https://www.nature.com/articles/s41591-024-03376-x",
        "keywords": ["血管", "人类"]
      }, {
        "title": "Cells of the adult human heart",
        "journal": "Nature",
        "category": "high",
        "year": "2020",
        "image": "/images/research/41586.png",
        "link": "https://www.nature.com/articles/s41586-020-2797-4",
        "keywords": ["心脏", "人类"]
      }, {
        "title": "Phenotypic Switching of Vascular Smooth Muscle Cells in Atherosclerosis",
        "journal": "J Am Heart Assoc",
        "category": "medium",
        "year": "2023",
        "image": "/images/research/10757534.jpg",
        "link": "https://www.ahajournals.org/doi/10.1161/JAHA.123.031121",
        "keywords": ["平滑肌细胞", "人类", "小鼠"]
      },
      {
        "title": "A high-resolution transcriptomic and spatial atlas of cell types in the whole mouse brain",
        "journal": "Nature",
        "category": "high",
        "year": "2023",
        "image": "/images/research/06812.png",
        "link": "https://www.nature.com/articles/s41586-023-06812-z",
        "keywords": ["全脑", "空间转录组", "小鼠"]
      }, {
        "title": "Langerhans cells are an essential component of the angiogenic niche during murine skin repair",
        "journal": "Dev Cell",
        "category": "medium",
        "year": "2022",
        "image": "/images/research/10848275.jpg",
        "link": "https://pmc.ncbi.nlm.nih.gov/articles/PMC10848275/",
        "keywords": ["内皮细胞", "小鼠"]
      }, {
        "title": "Dysregulation of brain and choroid plexus cell types in severe COVID-19",
        "journal": "Nature",
        "category": "high",
        "year": "2021",
        "image": "/images/research/8400927.png",
        "link": "https://pmc.ncbi.nlm.nih.gov/articles/PMC8400927/",
        "keywords": ["脑", "脉络丛细胞", "人类"]
      }, {
        "title": "Pan-cancer single-cell landscape of tumor-infiltrating T cells",
        "journal": "Science",
        "category": "high",
        "year": "2021",
        "image": "/images/research/101126.jpg",
        "link": "https://www.science.org/doi/10.1126/science.abe6474",
        "keywords": ["T细胞", "人类"]
      }, {
        "title": "Pan-cancer single-cell dissection reveals phenotypically distinct B cell subtypes",
        "journal": "Cell",
        "category": "high",
        "year": "2024",
        "image": "/images/research/0092.jpg",
        "link": "https://www.cell.com/cell/fulltext/S0092-8674(24)00712-8",
        "keywords": ["B细胞", "人类"]
      }, {
        "title": "An atlas of epithelial cell states and plasticity in lung adenocarcinoma",
        "journal": "Nature",
        "category": "high",
        "year": "2024",
        "image": "/images/research/07113.png",
        "link": "https://www.nature.com/articles/s41586-024-07113-9",
        "keywords": ["上皮细胞", "人类"]
      }, {
        "title": "Neurotoxic reactive astrocytes are induced by activated microglia",
        "journal": "Nature",
        "category": "high",
        "year": "2017",
        "image": "/images/research/5404890.jpg",
        "link": "https://pmc.ncbi.nlm.nih.gov/articles/PMC5404890/",
        "keywords": ["星形胶质细胞", "小鼠"]
      }, {
        "title": "Human microglial state dynamics in Alzheimer’s disease progression",
        "journal": "Cell",
        "category": "high",
        "year": "2023",
        "image": "/images/research/s0092.jpg",
        "link": "https://www.cell.com/cell/fulltext/S0092-8674(23)00971-6",
        "keywords": ["小胶质细胞", "人类"]
      }, {
        "title": "Fibroblast atlas: Shared and specific cell types across tissues",
        "journal": "Science Advances",
        "category": "high",
        "year": "2025",
        "image": "/images/research/0173.png",
        "link": "https://www.science.org/doi/10.1126/sciadv.ado0173",
        "keywords": ["成纤维细胞", "人类"]
      },   {
        "title": "A single-cell and spatial genomics atlas of human skin fibroblasts reveals shared disease-related fibroblast subtypes across tissues",
        "journal": "Nature Immunology",
        "category": "high",
        "year": "2025",
        "image": "/images/research/02267.png",
        "link": "https://www.nature.com/articles/s41590-025-02267-8",
        "keywords": ["成纤维细胞", "人类"]
      }, {
        "title": "High-resolution single-cell atlas reveals diversity and plasticity of tissue-resident neutrophils in non-small cell lung cancer",
        "journal": "Cancer cell",
        "category": "high",
        "year": "2022",
        "image": "/images/research/4f44c.png",
        "link": "https://www.cell.com/cell-reports/fulltext/S2211-1247(22)01543-8",
        "keywords": ["中性粒细胞", "人类"]
      }, {
        "title": "COVID-19 immune features revealed by a large-scale single-cell transcriptome atlas",
        "journal": "Cell",
        "category": "high",
        "year": "2021",
        "image": "/images/research/00148.jpg",
        "link": "https://www.cell.com/cell/fulltext/S0092-8674(21)00148-3",
        "keywords": ["免疫细胞", "人类"]
      }, {
        "title": "A pan-cancer single-cell panorama of human natural killer cells",
        "journal": "Cell",
        "category": "high",
        "year": "2023",
        "image": "/images/research/8674.jpg",
        "link": "https://www.cell.com/cell/fulltext/S0092-8674(23)00849-8",
        "keywords": ["NK细胞", "人类"]
      }, {
        "title": "Guidelines for T cell nomenclature",
        "journal": "Nature Reviews Immunology",
        "category": "high",
        "year": "2025",
        "image": "/images/research/01238-2.png",
        "link": "https://www.nature.com/articles/s41577-025-01238-2",
        "keywords": ["T细胞", "人类", "小鼠"]
      }, {
        "title": "Macrophages in cardiovascular diseases: molecular mechanisms and therapeutic targets",
        "journal": "Signal Transduction and Targeted Therapy",
        "category": "high",
        "year": "2024",
        "image": "/images/research/01840.webp",
        "link": "https://www.nature.com/articles/s41392-024-01840-1",
        "keywords": ["巨噬细胞", "人类", "小鼠"]
      }, {
        "title": "Integrated single-cell analysis-based classification of vascular mononuclear phagocytes in mouse and human atherosclerosis",
        "journal": "Cardiovascular Research",
        "category": "high",
        "year": "2023",
        "image": "/images/research/67447513.jpeg",
        "link": "https://academic.oup.com/cardiovascres/article/119/8/1676/6747513#google_vignette",
        "keywords": ["血管单核吞噬细胞", "小鼠", "人类"]
      },{
        "title": "Architecture of the neutrophil compartment",
        "journal": "Nature",
        "category": "high",
        "year": "2025",
        "image": "/images/research/182654_140.png",
        "link": "https://www.nature.com/articles/s41586-025-09807-0",
        "keywords": ["中性粒细胞", "小鼠", "人类"]
      }
    ],
    /* 分析工具*/
    'analysis-tools': [
      {
        title: "The single-cell transcriptional landscape of mammalian organogenesis",
        journal: "Nature",
        category: 'tool',
        year: '2019',
        image: 'https://readdy.ai/api/search-image?query=Single-cell%20transcriptional%20analysis%20of%20mammalian%20organogenesis%20showing%20developmental%20trajectories%20and%20pseudotime%20analysis%2C%20scientific%20visualization%20of%20organ%20development%2C%20cellular%20differentiation%20pathways%2C%20embryonic%20development%20with%20colorful%20cell%20fate%20mapping%20and%20temporal%20gene%20expression%20patterns%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=organogenesis-pseudotime-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41586-019-0969-x',
        analysisMethod: 'trajectory',
        toolLabel: 'Monocle3'        // ✅ 包名
      },
      {
        title: "Reversed graph embedding resolves complex single-cell trajectories",
        journal: "Nature Methods",
        category: 'tool',
        year: '2017',
        image: 'https://readdy.ai/api/search-image?query=Reversed%20graph%20embedding%20analysis%20of%20complex%20single-cell%20trajectories%20showing%20pseudotime%20reconstruction%2C%20scientific%20visualization%20of%20cellular%20differentiation%20paths%2C%20trajectory%20inference%20algorithms%20with%20colorful%20branching%20patterns%20and%20developmental%20lineages%2C%20computational%20biology%20workflow%20with%20cell%20fate%20mapping%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=reversed-graph-embedding-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/nmeth.4402',
        analysisMethod: 'trajectory',
        toolLabel: 'Monocle2'        // ✅ 包名

      },
      {
        title: "Slingshot: cell lineage and pseudotime inference for single-cell transcriptomics",
        journal: "BMC Genomics",
        category: 'tool',
        year: '2018',
        image: 'https://readdy.ai/api/search-image?query=Slingshot%20algorithm%20for%20cell%20lineage%20and%20pseudotime%20inference%20in%20single-cell%20transcriptomics%2C%20scientific%20visualization%20of%20cellular%20trajectory%20analysis%2C%20lineage%20tracing%20with%20branching%20developmental%20paths%2C%20computational%20method%20for%20pseudotime%20reconstruction%20with%20colorful%20cell%20differentiation%20patterns%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=slingshot-pseudotime-v1&orientation=landscape',
        link: 'https://bmcgenomics.biomedcentral.com/articles/10.1186/s12864-018-4772-0',
        analysisMethod: 'trajectory',
        toolLabel: 'Slingshot'        // ✅ 包名
      },
      {
        title: "Generalizing RNA velocity to transient cell states through dynamical modeling",
        journal: "Nature Biotechnology",
        category: 'tool',
        year: '2020',
        image: 'https://readdy.ai/api/search-image?query=RNA%20velocity%20analysis%20with%20dynamical%20modeling%20for%20transient%20cell%20states%2C%20scientific%20visualization%20of%20cellular%20dynamics%20and%20trajectory%20inference%2C%20pseudotime%20analysis%20with%20velocity%20vectors%2C%20single-cell%20transcriptomics%20temporal%20modeling%20with%20colorful%20cell%20state%20transitions%20and%20developmental%20trajectories%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=rna-velocity-dynamical-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41587-020-0591-3',
        analysisMethod: 'trajectory',
        toolLabel: 'Velocity'        // ✅ 包名
      },
      {
        title: "Improved reconstruction of single-cell developmental potential with CytoTRACE 2",
        journal: "Nature Methods",
        category: 'tool',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=CytoTRACE%202%20algorithm%20for%20single-cell%20developmental%20potential%20reconstruction%2C%20scientific%20visualization%20of%20cellular%20differentiation%20analysis%2C%20pseudotime%20inference%20with%20improved%20accuracy%2C%20developmental%20trajectory%20mapping%20with%20colorful%20cell%20state%20progression%20and%20lineage%20tracing%2C%20computational%20biology%20workflow%20with%20enhanced%20cell%20fate%20prediction%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=cytotrace2-developmental-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41592-025-02857-2',
        analysisMethod: 'trajectory',
        toolLabel: 'CytoTRACE2'        // ✅ 包名
      },
      {
        title: "CellRank 2: unified fate mapping in multiview single-cell data",
        journal: "Nature Methods",
        category: 'tool',
        year: '2024',
        image: 'https://readdy.ai/api/search-image?query=CellRank%202%20algorithm%20for%20unified%20fate%20mapping%20in%20multiview%20single-cell%20data%2C%20scientific%20visualization%20of%20cellular%20trajectory%20analysis%20and%20fate%20prediction%2C%20pseudotime%20inference%20with%20multimodal%20data%20integration%2C%20developmental%20lineage%20mapping%20with%20colorful%20cell%20fate%20trajectories%20and%20transition%20probabilities%2C%20computational%20biology%20workflow%20for%20unified%20fate%20analysis%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=cellrank2-fate-mapping-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41592-024-02303-9',
        analysisMethod: 'trajectory',
        toolLabel: 'CellRank2'        // ✅ 包名
      },
      {
        title: "Cell2fate infers RNA velocity modules to improve cell fate prediction",
        journal: "Nature Methods",
        category: 'tool',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=Cell2fate%20algorithm%20for%20RNA%20velocity%20modules%20and%20cell%20fate%20prediction%2C%20scientific%20visualization%20of%20cellular%20trajectory%20analysis%20with%20velocity-based%20fate%20inference%2C%20pseudotime%20analysis%20with%20improved%20prediction%20accuracy%2C%20developmental%20lineage%20mapping%20with%20colorful%20RNA%20velocity%20vectors%20and%20cell%20fate%20trajectories%2C%20computational%20biology%20workflow%20for%20enhanced%20fate%20prediction%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=cell2fate-rna-velocity-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41592-025-02608-3',
        analysisMethod: 'trajectory',
        toolLabel: 'Cell2fate'        // ✅ 包名
      },

      {
        title: "SCENIC: single-cell regulatory network inference and clustering",
        journal: "Nature Methods",
        category: 'tool',
        year: '2017',
        image: 'https://readdy.ai/api/search-image?query=SCENIC%20algorithm%20for%20single-cell%20regulatory%20network%20inference%20and%20clustering%2C%20scientific%20visualization%20of%20gene%20regulatory%20network%20analysis%2C%20transcription%20factor%20activity%20prediction%2C%20single-cell%20transcriptomics%20computational%20method%20with%20colorful%20regulatory%20network%20diagrams%20and%20cell%20clustering%20visualization%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=scenic-regulatory-network-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/nmeth.4463',
        analysisMethod: 'tf-prediction',
        toolLabel: 'SCENIC'        // ✅ 包名
      },
      {
        title: "SCENIC+: single-cell multiomic inference of enhancers and gene regulatory networks",
        journal: "Nature Methods",
        category: 'tool',
        year: '2023',
        image: 'https://readdy.ai/api/search-image?query=SCENIC%20plus%20algorithm%20for%20single-cell%20multiomic%20analysis%20showing%20enhancer%20inference%20and%20gene%20regulatory%20network%20reconstruction%2C%20scientific%20visualization%20of%20transcription%20factor%20activity%2C%20chromatin%20accessibility%20integration%20with%20gene%20expression%2C%20regulatory%20network%20diagrams%20with%20colorful%20transcription%20factor%20binding%20sites%20and%20target%20genes%2C%20computational%20biology%20workflow%20for%20multi-omics%20integration%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=scenic-plus-grn-v1&orientation=landscape',
        link: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC10482700/',
        analysisMethod: 'tf-prediction',
        toolLabel: 'SCENIC+'        // ✅ 包名
      },
      {
        title: "Reconstructing the regulatory programs underlying the phenotypic plasticity of neural cancers",
        journal: "Nature Communications",
        category: 'tool',
        year: '2024',
        image: 'https://readdy.ai/api/search-image?query=Neural%20cancer%20regulatory%20program%20reconstruction%20showing%20transcription%20factor%20networks%20and%20phenotypic%20plasticity%20analysis%2C%20scientific%20visualization%20of%20gene%20regulatory%20circuits%20in%20brain%20tumors%2C%20computational%20biology%20workflow%20with%20colorful%20transcription%20factor%20binding%20networks%20and%20cancer%20cell%20plasticity%20mechanisms%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=neural-cancer-regulatory-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41467-024-53954-3',
        analysisMethod: 'tf-prediction',
        toolLabel: 'Scregclust'        // ✅ 包名
      },
      {
        title: "Multitask benchmarking of single-cell multimodal omics integration methods",
        journal: "Nature Methods",
        category: 'tool',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=Computational%20benchmarking%20analysis%20of%20single-cell%20multimodal%20omics%20integration%20methods%2C%20scientific%20visualization%20showing%20algorithm%20comparison%2C%20method%20evaluation%20metrics%2C%20bioinformatics%20workflow%20diagrams%20with%20colorful%20performance%20charts%20and%20integration%20networks%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=multimodal-benchmarking-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41592-025-02856-3',
        analysisMethod: 'multiomics-integration',
        toolLabel: 'scMultiBench'        // ✅ 包名
      },
      {
        title: "DIABLO: an integrative approach for identifying key molecular drivers from multi-omics assays",
        journal: "Bioinformatics",
        category: 'tool',
        year: '2019',
        image: 'https://readdy.ai/api/search-image?query=DIABLO%20multi-omics%20integration%20algorithm%20visualization%20showing%20molecular%20driver%20identification%2C%20scientific%20illustration%20of%20integrative%20bioinformatics%20approach%2C%20multi-dimensional%20data%20analysis%20with%20colorful%20network%20diagrams%20and%20molecular%20pathway%20integration%2C%20computational%20biology%20workflow%20with%20data%20matrices%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=diablo-multiomics-v1&orientation=landscape',
        link: 'https://doi.org/10.1093/bioinformatics/bty1054',
        analysisMethod: 'multiomics-integration',
        toolLabel: 'DIABLO'        // ✅ 包名
      },
      {
        title: "Resolving tissue complexity by multimodal spatial omics modeling with MISO",
        journal: "Nature Methods",
        category: 'tool',
        year: '2025',
        image: '/images/research/2024_2574.webp',
        link: 'https://www.nature.com/articles/s41592-024-02574-2',
        analysisMethod: 'multiomics-integration',
        toolLabel: 'MISO'        // ✅ 包名
      },
      {
        title: "Integrating cross-sample and cross-modal data for spatial transcriptomics and metabolomics with SpatialMETA",
        journal: "Nature Communications",
        category: 'tool',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=SpatialMETA%20algorithm%20for%20integrating%20spatial%20transcriptomics%20and%20metabolomics%20data%20showing%20cross-sample%20and%20cross-modal%20integration%2C%20scientific%20visualization%20of%20spatial%20multi-omics%20analysis%2C%20tissue%20spatial%20mapping%20with%20gene%20expression%20and%20metabolite%20data%20overlay%2C%20computational%20biology%20workflow%20with%20colorful%20spatial%20data%20integration%20and%20metabolic%20pathway%20networks%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=spatialmeta-integration-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41467-025-63915-z',
        analysisMethod: 'multiomics-integration',
        toolLabel: 'SpatialMETA'        // ✅ 包名
      },
      {
        title: "Metabolic modeling of single Th17 cells reveals regulators of autoimmunity",
        journal: "Cell",
        category: 'tool',
        year: '2021',
        image: 'https://readdy.ai/api/search-image?query=Single-cell%20metabolic%20modeling%20of%20Th17%20cells%20showing%20autoimmunity%20regulators%20and%20metabolic%20pathways%2C%20scientific%20visualization%20of%20immune%20cell%20metabolism%2C%20T%20helper%2017%20cell%20biology%20with%20metabolic%20network%20analysis%2C%20autoimmune%20disease%20mechanisms%20with%20colorful%20metabolic%20flux%20diagrams%20and%20cellular%20energy%20pathways%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=th17-metabolic-modeling-v1&orientation=landscape',
        link: 'https://www.cell.com/cell/fulltext/S0092-8674(21)00700-5?_returnURL=https%3A%2F%2Flinkinghub.elsevier.com%2Fretrieve%2Fpii%2FS0092867421007005%3Fshowall%3Dtrue',
        analysisMethod: 'metabolomics',
        toolLabel: 'Compass'        // ✅ 包名
      },
      {
        title: "MBROLE3: improved functional enrichment of chemical compounds for metabolomics data analysis",
        journal: "Nucleic Acids Research",
        category: 'tool',
        year: '2023',
        image: 'https://readdy.ai/api/search-image?query=MBROLE3%20metabolomics%20data%20analysis%20tool%20showing%20functional%20enrichment%20of%20chemical%20compounds%2C%20scientific%20visualization%20of%20metabolite%20pathway%20analysis%2C%20bioinformatics%20workflow%20for%20metabolomics%20research%20with%20colorful%20compound%20classification%20and%20enrichment%20networks%2C%20computational%20biology%20interface%20with%20metabolic%20pathway%20diagrams%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=mbrole3-metabolomics-v1&orientation=landscape',
        link: 'https://academic.oup.com/nar/article/51/W1/W305/7161529?login=false',
        analysisMethod: 'metabolomics',
        toolLabel: 'MBROLE3'        // ✅ 包名
      },
      {
        title: "MetaboAnalystR 4.0: a unified LC-MS workflow for global metabolomics",
        journal: "Nature Communications",
        category: 'tool',
        year: '2024',
        image: 'https://readdy.ai/api/search-image?query=MetaboAnalystR%204.0%20LC-MS%20workflow%20for%20global%20metabolomics%20analysis%2C%20scientific%20visualization%20of%20unified%20metabolomics%20pipeline%2C%20mass%20spectrometry%20data%20processing%20workflow%2C%20computational%20metabolomics%20platform%20with%20colorful%20analytical%20charts%20and%20metabolite%20identification%20networks%2C%20modern%20bioinformatics%20interface%20with%20LC-MS%20integration%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=metaboanalystr4-workflow-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41467-024-48009-6',
        analysisMethod: 'metabolomics',
        toolLabel: 'MetaboAnalystR 4.0'        // ✅ 包名
      },
      {
        title: "scCODA is a Bayesian model for compositional single-cell data analysis",
        journal: "Nature Communications",
        impactFactor: 14.7,
        category: 'tool',
        year: '2021',
        image: 'https://readdy.ai/api/search-image?query=scCODA%20Bayesian%20model%20for%20compositional%20single-cell%20data%20analysis%20showing%20cell%20abundance%20estimation%20and%20statistical%20modeling%2C%20scientific%20visualization%20of%20cellular%20composition%20analysis%2C%20probabilistic%20modeling%20of%20cell%20type%20proportions%20with%20colorful%20statistical%20diagrams%20and%20compositional%20data%20visualization%2C%20computational%20biology%20workflow%20with%20Bayesian%20inference%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=sccoda-bayesian-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41467-021-27150-6',
        analysisMethod: 'cell-abundance',
        toolLabel: 'scCODA'        // ✅ 包名
      },
      {
        title: "Differential abundance testing on single-cell data using k-nearest neighbor graphs",
        journal: "Nature Biotechnology",
        category: 'tool',
        year: '2022',
        image: 'https://readdy.ai/api/search-image?query=Milo%20algorithm%20for%20differential%20abundance%20testing%20on%20single-cell%20data%20using%20k-nearest%20neighbor%20graphs%2C%20scientific%20visualization%20of%20cellular%20neighborhood%20analysis%2C%20graph-based%20statistical%20framework%20for%20abundance%20testing%2C%20single-cell%20data%20analysis%20with%20colorful%20KNN%20network%20diagrams%20and%20differential%20abundance%20visualization%2C%20computational%20biology%20workflow%20with%20neighborhood%20identification%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=milo-knn-abundance-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41587-021-01033-z',
        analysisMethod: 'cell-abundance',
        toolLabel: 'Milo'        // ✅ 包名
      },
      {
        title: "Inference and analysis of cell-cell communication using CellChat",
        journal: "Nature Communications",
        impactFactor: 14.7,
        category: 'tool',
        year: '2021',
        image: 'https://readdy.ai/api/search-image?query=CellChat%20algorithm%20for%20cell-cell%20communication%20inference%20and%20analysis%2C%20scientific%20visualization%20of%20intercellular%20signaling%20networks%2C%20cellular%20interaction%20pathways%20with%20ligand-receptor%20pairs%2C%20communication%20network%20analysis%20with%20colorful%20cell%20type%20interactions%20and%20signaling%20strength%20visualization%2C%20computational%20biology%20workflow%20for%20cell%20communication%20analysis%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=cellchat-communication-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41467-021-21246-9',
        analysisMethod: 'cell-communication',
        toolLabel: 'CellChat'        // ✅ 包名
      },
      {
        title: "Single-cell reconstruction of the early maternal–fetal interface in humans",
        journal: "Nature",
        category: 'tool',
        year: '2018',
        image: 'https://readdy.ai/api/search-image?query=Single-cell%20reconstruction%20of%20maternal-fetal%20interface%20showing%20placental%20development%20and%20cellular%20interactions%2C%20scientific%20visualization%20of%20pregnancy%20biology%2C%20maternal-fetal%20cellular%20interactions%2C%20trophoblast%20and%20decidual%20cell%20communication%20networks%20with%20colorful%20cell%20type%20mapping%20and%20developmental%20biology%20pathways%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=maternal-fetal-interface-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41586-018-0698-6',
        analysisMethod: 'cell-communication',
        toolLabel: 'CellPhoneDB'        // ✅ 包名
      },
      {
        title: "Inferring pattern-driving intercellular flows from single-cell and spatial transcriptomics",
        journal: "Nature Methods",
        category: 'tool',
        year: '2024',
        image: 'https://readdy.ai/api/search-image?query=Intercellular%20communication%20analysis%20from%20single-cell%20and%20spatial%20transcriptomics%20showing%20pattern-driving%20cellular%20flows%2C%20scientific%20visualization%20of%20cell-cell%20interaction%20networks%2C%20spatial%20gene%20expression%20patterns%20with%20communication%20pathways%2C%20computational%20biology%20workflow%20for%20intercellular%20signaling%20analysis%20with%20colorful%20flow%20diagrams%20and%20cellular%20interaction%20maps%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=intercellular-flows-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41592-024-02380-w',
        analysisMethod: 'cell-communication',
        toolLabel: 'FlowSig'        // ✅ 包名
      },
      {
        title: "CellCall: integrating paired ligand–receptor and transcription factor activities for cell–cell communication",
        journal: "Nucleic Acids Research",
        category: 'tool',
        year: '2021',
        image: 'https://readdy.ai/api/search-image?query=CellCall%20algorithm%20for%20cell-cell%20communication%20analysis%20integrating%20ligand-receptor%20pairs%20and%20transcription%20factor%20activities%2C%20scientific%20visualization%20of%20cellular%20signaling%20networks%2C%20paired%20molecular%20interactions%20with%20transcriptional%20regulation%2C%20computational%20biology%20workflow%20for%20intercellular%20communication%20analysis%20with%20colorful%20signaling%20pathway%20diagrams%20and%20regulatory%20networks%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=cellcall-communication-v1&orientation=landscape',
        link: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC8421219/',
        analysisMethod: 'cell-communication',
        toolLabel: 'CellCall'        // ✅ 包名
      },
      {
        title: "NicheNet: modeling intercellular communication by linking ligands to target genes",
        journal: "Nature Methods",
        category: 'tool',
        year: '2019',
        image: 'https://readdy.ai/api/search-image?query=NicheNet%20algorithm%20for%20modeling%20intercellular%20communication%20by%20linking%20ligands%20to%20target%20genes%2C%20scientific%20visualization%20of%20cell-cell%20signaling%20networks%2C%20ligand-target%20gene%20regulatory%20connections%2C%20computational%20biology%20workflow%20for%20intercellular%20communication%20modeling%20with%20colorful%20signaling%20pathway%20diagrams%20and%20gene%20regulatory%20networks%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=nichenet-communication-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41592-019-0667-5',
        analysisMethod: 'cell-communication',
        toolLabel: 'NicheNet'        // ✅ 包名
      },
      {
        title: "Scrublet: Computational Identification of Cell Doublets in Single-Cell Transcriptomic Data",
        journal: "Cell Systems",
        category: 'tool',
        year: '2018',
        image: 'https://readdy.ai/api/search-image?query=Scrublet%20algorithm%20for%20computational%20identification%20of%20cell%20doublets%20in%20single-cell%20RNA%20sequencing%20data%2C%20scientific%20visualization%20of%20quality%20control%20analysis%2C%20doublet%20detection%20workflow%20showing%20multiplet%20artifacts%20identification%2C%20single-cell%20transcriptomics%20data%20preprocessing%20with%20colorful%20cell%20classification%20and%20quality%20metrics%20visualization%2C%20computational%20biology%20pipeline%20for%20data%20cleaning%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=scrublet-doublet-detection-v1&orientation=landscape',
        link: 'https://www.cell.com/cell-systems/fulltext/S2405-4712(18)30474-5',
        analysisMethod: 'quality-control',
        toolLabel: 'Scrublet'        // ✅ 包名
      },
      {
        title: "Decontamination of ambient RNA in single-cell RNA-seq with DecontX",
        journal: "Genome Biology",
        category: 'tool',
        year: '2020',
        image: 'https://readdy.ai/api/search-image?query=DecontX%20algorithm%20for%20decontamination%20of%20ambient%20RNA%20in%20single-cell%20RNA%20sequencing%2C%20scientific%20visualization%20of%20RNA%20contamination%20removal%2C%20quality%20control%20workflow%20showing%20ambient%20RNA%20detection%20and%20correction%2C%20single-cell%20transcriptomics%20data%20preprocessing%20with%20colorful%20contamination%20analysis%20and%20decontamination%20process%2C%20computational%20biology%20pipeline%20for%20data%20purification%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=decontx-decontamination-v1&orientation=landscape',
        link: 'https://genomebiology.biomedcentral.com/articles/10.1186/s13059-020-1950-6',
        analysisMethod: 'quality-control',
        toolLabel: 'DecontX'        // ✅ 包名
      },
      {
        title: "DoubletFinder: Doublet Detection in Single-Cell RNA Sequencing Data Using Artificial Nearest Neighbors",
        journal: "Cell Systems",
        category: 'tool',
        year: '2019',
        image: 'https://readdy.ai/api/search-image?query=DoubletFinder%20algorithm%20for%20doublet%20detection%20in%20single-cell%20RNA%20sequencing%20using%20artificial%20nearest%20neighbors%2C%20scientific%20visualization%20of%20quality%20control%20analysis%2C%20doublet%20identification%20workflow%20showing%20artificial%20neighbor%20classification%2C%20single-cell%20transcriptomics%20data%20preprocessing%20with%20colorful%20cell%20classification%20and%20doublet%20detection%20metrics%2C%20computational%20biology%20pipeline%20for%20multiplet%20removal%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=doubletfinder-detection-v1&orientation=landscape',
        link: 'https://www.cell.com/cell-systems/fulltext/S2405-4712(19)30073-0',
        analysisMethod: 'quality-control',
        toolLabel: 'DoubletFinder'        // ✅ 包名
      },
      {
        title: "Seurat v5 enables integrated analysis of multimodal single cell data",
        journal: "Nature Biotechnology",
        category: 'tool',
        year: '2024',
        image: 'https://readdy.ai/api/search-image?query=Seurat%20v5%20algorithm%20for%20integrated%20analysis%20of%20multimodal%20single-cell%20data%2C%20scientific%20visualization%20of%20data%20integration%20workflow%2C%20multiple%20omics%20layers%20integration%20including%20RNA%2C%20ATAC%2C%20protein%20data%2C%20computational%20biology%20pipeline%20with%20colorful%20data%20harmonization%20and%20batch%20correction%20visualization%2C%20unified%20single-cell%20analysis%20framework%20with%20integrated%20clustering%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=seurat-v5-integration-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41592-023-01767-y',
        analysisMethod: 'data-integration',
        toolLabel: 'Seurat v5'        // ✅ 包名
      },
      {
        title: "Comprehensive Integration of Single-Cell Data",
        journal: "Cell",
        category: 'tool',
        year: '2019',
        image: 'https://readdy.ai/api/search-image?query=Comprehensive%20single-cell%20data%20integration%20showing%20batch%20correction%20and%20dataset%20harmonization%2C%20scientific%20visualization%20of%20integration%20algorithms%2C%20multiple%20single-cell%20datasets%20merging%20with%20anchor-based%20integration%2C%20computational%20biology%20workflow%20with%20colorful%20cell%20type%20alignment%20across%20batches%2C%20unified%20cell%20atlas%20construction%20with%20integrated%20analysis%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=seurat-integration-comprehensive-v1&orientation=landscape',
        link: 'https://www.cell.com/cell/fulltext/S0092-8674(19)30559-8',
        analysisMethod: 'data-integration',
        toolLabel: 'Seurat'        // ✅ 包名
      },
      {
        title: "Integrated analysis of multimodal single-cell data",
        journal: "Cell",
        category: 'tool',
        year: '2021',
        image: 'https://readdy.ai/api/search-image?query=Multimodal%20single-cell%20data%20integration%20showing%20weighted%20nearest%20neighbor%20analysis%2C%20scientific%20visualization%20of%20multi-omics%20integration%20workflow%2C%20RNA%20and%20protein%20data%20integration%20with%20CITE-seq%2C%20computational%20biology%20pipeline%20with%20colorful%20modality-specific%20clustering%20and%20joint%20embedding%2C%20integrated%20multimodal%20analysis%20framework%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=wnn-multimodal-integration-v1&orientation=landscape',
        link: 'https://www.cell.com/cell/fulltext/S0092-8674(21)00583-3',
        analysisMethod: 'data-integration',
        toolLabel: 'Seurat'        // ✅ 包名
      },
      {
        title: "LIGER: Integrating and analyzing multiple single-cell datasets",
        journal: "Nature Protocols",
        category: 'tool',
        year: '2020',
        image: 'https://readdy.ai/api/search-image?query=LIGER%20algorithm%20for%20integrating%20multiple%20single-cell%20datasets%20showing%20joint%20matrix%20factorization%2C%20scientific%20visualization%20of%20cross-dataset%20integration%2C%20batch%20effect%20removal%20with%20shared%20and%20dataset-specific%20factors%2C%20computational%20biology%20workflow%20with%20colorful%20integrated%20cell%20clustering%20and%20alignment%2C%20multi-dataset%20harmonization%20framework%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=liger-integration-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41596-020-0391-8',
        analysisMethod: 'data-integration',
        toolLabel: 'LIGER'        // ✅ 包名
      },
      {
        title: "Harmony: Fast, sensitive and accurate integration of single-cell data",
        journal: "Nature Methods",
        category: 'tool',
        year: '2019',
        image: 'https://readdy.ai/api/search-image?query=Harmony%20algorithm%20for%20fast%20and%20accurate%20single-cell%20data%20integration%2C%20scientific%20visualization%20of%20iterative%20clustering%20and%20batch%20correction%2C%20multiple%20dataset%20harmonization%20with%20diversity-based%20integration%2C%20computational%20biology%20workflow%20with%20colorful%20batch-corrected%20embeddings%20and%20aligned%20cell%20populations%2C%20efficient%20integration%20framework%20preserving%20biological%20variation%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=harmony-integration-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41592-019-0619-0',
        analysisMethod: 'data-integration',
        toolLabel: 'Harmony'        // ✅ 包名
      },
      {
        title: "scVI: Deep generative modeling for single-cell transcriptomics",
        journal: "Nature Methods",
        category: 'tool',
        year: '2018',
        image: 'https://readdy.ai/api/search-image?query=scVI%20deep%20generative%20modeling%20for%20single-cell%20transcriptomics%20showing%20variational%20autoencoder%20architecture%2C%20scientific%20visualization%20of%20deep%20learning-based%20integration%2C%20probabilistic%20modeling%20of%20gene%20expression%20with%20batch%20correction%2C%20computational%20biology%20workflow%20with%20colorful%20latent%20space%20representation%20and%20integrated%20cell%20embeddings%2C%20neural%20network%20framework%20for%20single-cell%20analysis%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=scvi-deep-learning-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41592-018-0229-2',
        analysisMethod: 'data-integration',
        toolLabel: 'scVI'        // ✅ 包名
      },
      {
        title: "Shortcomings of silhouette in single-cell integration benchmarking",
        journal: "Nature Biotechnology",
        category: 'tool',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=Single-cell%20integration%20benchmarking%20analysis%20showing%20silhouette%20metric%20evaluation%20and%20batch%20effect%20assessment%2C%20scientific%20visualization%20of%20clustering%20quality%20metrics%2C%20computational%20biology%20workflow%20with%20colorful%20benchmark%20comparison%20charts%20and%20integration%20evaluation%20methods%2C%20data%20integration%20assessment%20framework%20with%20statistical%20analysis%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=silhouette-benchmarking-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41587-025-02743-4',
        analysisMethod: 'data-integration',
        toolLabel: 'scib'        // ✅ 包名
      },
      {
        title: "Benchmarking atlas-level data integration in single-cell genomics",
        journal: "Nature Methods",
        category: 'tool',
        year: '2022',
        image: 'https://readdy.ai/api/search-image?query=Atlas-level%20single-cell%20genomics%20data%20integration%20benchmarking%20showing%20comprehensive%20method%20comparison%2C%20scientific%20visualization%20of%20integration%20algorithm%20evaluation%2C%20computational%20biology%20workflow%20with%20colorful%20benchmark%20metrics%20and%20performance%20analysis%2C%20large-scale%20data%20integration%20assessment%20with%20statistical%20comparison%20charts%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=atlas-integration-benchmarking-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41592-021-01336-8',
        analysisMethod: 'data-integration',
        toolLabel: 'sciB'        // ✅ 包名
      },
      {
        title: "Method of moments framework for differential expression analysis of single-cell RNA sequencing data",
        journal: "Cell",
        category: 'tool',
        year: '2024',
        image: 'https://readdy.ai/api/search-image?query=Method%20of%20moments%20framework%20for%20single-cell%20RNA%20sequencing%20differential%20expression%20analysis%2C%20scientific%20visualization%20of%20statistical%20modeling%20approach%2C%20computational%20biology%20workflow%20with%20colorful%20differential%20expression%20analysis%20charts%2C%20moment-based%20statistical%20methods%20with%20gene%20expression%20data%20visualization%2C%20mathematical%20framework%20for%20single-cell%20analysis%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=moments-differential-expression-v1&orientation=landscape',
        link: 'https://www.cell.com/cell/fulltext/S0092-8674(24)01144-9',
        analysisMethod: 'other',
        toolLabel: 'memento'        // ✅ 包名
      },
      {
        title: "Dissecting cell identity via network inference and in silico gene perturbation",
        journal: "Nature",
        category: 'tool',
        year: '2023',
        image: 'https://readdy.ai/api/search-image?query=Cell%20identity%20analysis%20through%20network%20inference%20and%20in%20silico%20gene%20perturbation%20showing%20computational%20framework%20for%20cell%20type%20ranking%20and%20selection%2C%20scientific%20visualization%20of%20gene%20regulatory%20networks%20and%20cellular%20perturbation%20analysis%2C%20single-cell%20transcriptomics%20with%20network%20reconstruction%2C%20colorful%20gene%20interaction%20networks%20and%20perturbation%20effects%20visualization%2C%20computational%20cell%20biology%20framework%20with%20molecular%20pathway%20analysis%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=cell-identity-perturbation-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41586-022-05688-9',
        analysisMethod: 'cell-perturbation',
        toolLabel: 'CellOracle'        // ✅ 包名
      },
      {
        title: "Robust identification of perturbed cell types in single-cell RNA-seq data",
        journal: "Nature Communications",
        category: 'tool',
        year: '2024',
        image: 'https://readdy.ai/api/search-image?query=Robust%20identification%20of%20perturbed%20cell%20types%20in%20single-cell%20RNA%20sequencing%20data%20showing%20computational%20algorithm%20for%20perturbation%20detection%2C%20scientific%20visualization%20of%20cellular%20response%20analysis%2C%20statistical%20framework%20for%20identifying%20disturbed%20cell%20populations%2C%20colorful%20single-cell%20data%20analysis%20with%20perturbation%20effects%20mapping%20and%20cell%20type%20classification%2C%20computational%20biology%20workflow%20with%20robust%20detection%20methods%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=robust-perturbation-detection-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41467-024-51649-3',
        analysisMethod: 'cell-perturbation',
        toolLabel: 'Augur'        // ✅ 包名
      },
      {
        title: "Cell type prioritization in single-cell data",
        journal: "Nature Biotechnology",
        category: 'tool',
        year: '2021',
        image: 'https://readdy.ai/api/search-image?query=Cell%20type%20prioritization%20in%20single-cell%20data%20analysis%20showing%20computational%20framework%20for%20cell%20type%20ranking%20and%20selection%2C%20scientific%20visualization%20of%20cellular%20hierarchy%20analysis%2C%20priority%20scoring%20algorithms%20with%20colorful%20cell%20type%20classification%20and%20ranking%20systems%2C%20computational%20biology%20workflow%20for%20cell%20type%20importance%20assessment%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=cell-type-prioritization-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41587-020-0605-1',
        analysisMethod: 'cell-perturbation',
        toolLabel: 'scDist'        // ✅ 包名
      },
      {
        title: "Reproducible single-cell annotation of programs underlying T cell subsets, activation states and functions",
        journal: "Nature Methods",
        category: 'tool',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=Reproducible%20single-cell%20annotation%20of%20T%20cell%20programs%20showing%20computational%20framework%20for%20T%20cell%20subset%20identification%20and%20activation%20state%20analysis%2C%20scientific%20visualization%20of%20T%20cell%20biology%20with%20functional%20annotation%2C%20single-cell%20transcriptomics%20analysis%20with%20colorful%20T%20cell%20subset%20classification%20and%20activation%20marker%20identification%2C%20computational%20biology%20workflow%20for%20reproducible%20cell%20annotation%20with%20T%20cell%20functional%20programs%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=tcell-annotation-programs-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41592-025-02793-1',
        analysisMethod: 'cell-annotation',
        toolLabel: 'starCAT'        // ✅ 包名
      },
      {
        title: "An automatic annotation tool and reference database for T cell subtypes and states at single-cell resolution",
        journal: "Science Bulletin",
        category: 'tool',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=Automatic%20T%20cell%20annotation%20tool%20and%20reference%20database%20showing%20computational%20framework%20for%20T%20cell%20subtype%20identification%20at%20single-cell%20resolution%2C%20scientific%20visualization%20of%20automated%20cell%20annotation%20workflow%2C%20T%20cell%20biology%20with%20reference%20database%20integration%2C%20single-cell%20transcriptomics%20analysis%20with%20colorful%20T%20cell%20classification%20and%20automated%20annotation%20pipeline%2C%20computational%20biology%20framework%20for%20T%20cell%20state%20identification%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=tcell-automatic-annotation-v1&orientation=landscape',
        link: 'https://www.sciencedirect.com/science/article/pii/S2095927325002889?via%3Dihub',
        analysisMethod: 'cell-annotation',
        toolLabel: 'STCAT'        // ✅ 包名
      },
      {
        title: "Consensus prediction of cell type labels in single-cell data with popV",
        journal: "Nature Genetics",
        category: 'tool',
        year: '2024',
        image: 'https://readdy.ai/api/search-image?query=popV%20algorithm%20for%20consensus%20prediction%20of%20cell%20type%20labels%20in%20single-cell%20data%20showing%20computational%20framework%20for%20cell%20annotation%2C%20scientific%20visualization%20of%20cell%20type%20classification%20with%20multiple%20annotation%20methods%20integration%2C%20consensus%20prediction%20workflow%20with%20colorful%20cell%20type%20identification%20and%20label%20assignment%2C%20computational%20biology%20pipeline%20for%20accurate%20cell%20annotation%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=popv-cell-annotation-v1&orientation=landscape',
        link: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC11631762/',
        analysisMethod: 'cell-annotation',
        toolLabel: 'popV'        // ✅ 包名
      },
      {
        title: "Cross-tissue immune cell analysis reveals tissue-specific features in humans",
        journal: "Science",
        category: 'tool',
        year: '2022',
        image: 'https://readdy.ai/api/search-image?query=Cross-tissue%20immune%20cell%20analysis%20showing%20tissue-specific%20features%20in%20human%20cell%20atlas%2C%20scientific%20visualization%20of%20immune%20cell%20populations%20across%20multiple%20tissues%2C%20computational%20biology%20workflow%20with%20colorful%20cell%20type%20annotation%20and%20tissue%20comparison%2C%20single-cell%20transcriptomics%20analysis%20with%20immune%20cell%20classification%20across%20organs%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=cross-tissue-immune-annotation-v1&orientation=landscape',
        link: 'https://www.science.org/doi/10.1126/science.abl5197',
        analysisMethod: 'cell-annotation',
        toolLabel: 'CellTypist'        // ✅ 包名
      },
      {
        title: "Reference-based analysis of lung single-cell sequencing reveals a transitional profibrotic macrophage",
        journal: "Nature Immunology",
        category: 'tool',
        year: '2019',
        image: 'https://readdy.ai/api/search-image?query=Reference-based%20analysis%20of%20lung%20single-cell%20sequencing%20showing%20transitional%20profibrotic%20macrophage%20identification%2C%20scientific%20visualization%20of%20lung%20immune%20cell%20analysis%2C%20macrophage%20cell%20type%20annotation%20and%20classification%2C%20pulmonary%20fibrosis%20cellular%20mechanisms%20with%20colorful%20cell%20type%20identification%20and%20reference-based%20annotation%20workflow%2C%20computational%20biology%20framework%20for%20lung%20cell%20atlas%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=lung-profibrotic-macrophage-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41590-018-0276-y',
        analysisMethod: 'cell-annotation',
        toolLabel: 'SingleR'        // ✅ 包名
      },

      {
        title: "CellNEST reveals cell–cell relay networks using attention mechanisms on spatial transcriptomics",
        journal: "Nature Methods",
        category: 'tool',
        year: '2025',
        image: 'https://readdy.ai/api/search-image?query=CellNEST%20algorithm%20for%20spatial%20transcriptomics%20showing%20cell-cell%20relay%20networks%20with%20attention%20mechanisms%2C%20scientific%20visualization%20of%20spatial%20gene%20expression%20analysis%2C%20cellular%20communication%20networks%20in%20tissue%20architecture%2C%20computational%20biology%20workflow%20with%20colorful%20spatial%20mapping%20and%20attention-based%20network%20inference%2C%20tissue%20spatial%20organization%20with%20cell%20interaction%20patterns%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=cellnest-spatial-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41592-025-02721-3',
        analysisMethod: 'spatial-transcriptomics',
        toolLabel: 'CellNEST'        // ✅ 包名
      },

      {
        title: "SCS: cell segmentation for high-resolution spatial transcriptomics",
        journal: "Nature Methods",
        category: 'tool',
        year: '2023',
        image: 'https://readdy.ai/api/search-image?query=SCS%20cell%20segmentation%20algorithm%20for%20high-resolution%20spatial%20transcriptomics%20showing%20subcellular%20resolution%20analysis%2C%20scientific%20visualization%20of%20cell%20boundary%20detection%20in%20tissue%20sections%2C%20transformer%20neural%20network%20for%20spot%20assignment%20to%20cells%2C%20computational%20biology%20workflow%20with%20colorful%20spatial%20gene%20expression%20mapping%20and%20cell%20segmentation%20accuracy%2C%20tissue%20architecture%20with%20precise%20cellular%20boundaries%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=scs-cell-segmentation-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41592-023-01939-3',
        analysisMethod: 'spatial-transcriptomics',
        toolLabel: 'SCS'        // ✅ 包名
      },

      {
        title: "BANKSY unifies cell typing and tissue domain segmentation for scalable spatial omics data analysis",
        journal: "Nature Genetics",
        category: 'tool',
        year: '2024',
        image: 'https://readdy.ai/api/search-image?query=BANKSY%20algorithm%20for%20spatial%20omics%20data%20analysis%20showing%20unified%20cell%20typing%20and%20tissue%20domain%20segmentation%2C%20scientific%20visualization%20of%20spatial%20transcriptomics%20with%20tissue%20architecture%20mapping%2C%20computational%20biology%20workflow%20with%20colorful%20spatial%20gene%20expression%20patterns%20and%20cell%20type%20identification%2C%20scalable%20spatial%20data%20analysis%20framework%20with%20tissue%20domain%20boundaries%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=banksy-spatial-omics-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41588-024-01664-3',
        analysisMethod: 'spatial-transcriptomics',
        toolLabel: 'BANKSY'        // ✅ 包名
      },

      {
        title: "Spatial transcriptomics at subspot resolution with BayesSpace",
        journal: "Nature Biotechnology",
        category: 'tool',
        year: '2021',
        image: 'https://readdy.ai/api/search-image?query=BayesSpace%20algorithm%20for%20spatial%20transcriptomics%20at%20subspot%20resolution%20showing%20enhanced%20spatial%20gene%20expression%20analysis%2C%20scientific%20visualization%20of%20Bayesian%20statistical%20framework%20for%20spatial%20clustering%2C%20tissue%20architecture%20with%20improved%20resolution%20spatial%20mapping%2C%20computational%20biology%20workflow%20with%20colorful%20spatial%20domain%20identification%20and%20gene%20expression%20patterns%20at%20subcellular%20resolution%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=bayesspace-subspot-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41587-021-00935-2',
        analysisMethod: 'spatial-transcriptomics',
        toolLabel: 'BayesSpace'        // ✅ 包名
      },

      {
        title: "Perturbation-response genes reveal signaling footprints in cancer gene expression",
        journal: "Nature Communications",
        category: 'tool',
        year: '2018',
        image: 'https://readdy.ai/api/search-image?query=Perturbation-response%20genes%20analysis%20revealing%20signaling%20footprints%20in%20cancer%20gene%20expression%2C%20scientific%20visualization%20of%20cellular%20signaling%20pathways%20and%20perturbation%20effects%2C%20spatial%20transcriptomics%20with%20signal%20transduction%20networks%2C%20computational%20biology%20workflow%20with%20colorful%20pathway%20activity%20mapping%20and%20gene%20expression%20patterns%20in%20cancer%20tissue%20architecture%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=perturbation-signaling-spatial-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41467-017-02391-6',
        analysisMethod: 'spatial-transcriptomics',
        toolLabel: 'PROGENy'        // ✅ 包名
      },

      {
        title: "Explainable multiview framework for dissecting spatial relationships from highly multiplexed data",
        journal: "Genome Biology",
        category: 'tool',
        year: '2022',
        image: 'https://readdy.ai/api/search-image?query=Explainable%20multiview%20framework%20for%20spatial%20relationships%20analysis%20in%20highly%20multiplexed%20data%2C%20scientific%20visualization%20of%20multiview%20learning%20and%20spatial%20data%20integration%2C%20computational%20biology%20workflow%20with%20colorful%20spatial%20relationship%20mapping%20and%20multiplexed%20data%20visualization%2C%20explainable%20AI%20framework%20for%20spatial%20omics%20analysis%20with%20interpretable%20results%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=multiview-spatial-framework-v1&orientation=landscape',
        link: 'https://genomebiology.biomedcentral.com/articles/10.1186/s13059-022-02663-5',
        analysisMethod: 'spatial-transcriptomics',
        toolLabel: 'MISTy'        // ✅ 包名
      },

      {
        title: "Robust mapping of spatiotemporal trajectories and cell–cell interactions in healthy and diseased tissues",
        journal: "Nature Communications",
        category: 'tool',
        year: '2023',
        image: 'https://readdy.ai/api/search-image?query=Robust%20mapping%20of%20spatiotemporal%20trajectories%20and%20cell-cell%20interactions%20in%20healthy%20and%20diseased%20tissues%2C%20scientific%20visualization%20of%20spatial%20transcriptomics%20with%20temporal%20dynamics%2C%20tissue%20architecture%20analysis%20showing%20cellular%20interactions%20over%20time%2C%20computational%20biology%20workflow%20with%20colorful%20spatial%20mapping%20and%20trajectory%20visualization%2C%20disease%20progression%20analysis%20with%20cell%20communication%20networks%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=robust-spatiotemporal-mapping-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41467-023-43120-6',
        analysisMethod: 'spatial-transcriptomics',
        toolLabel: 'stLearn'        // ✅ 包名
      },

      {
        title: "SEACells infers transcriptional and epigenomic cellular states from single-cell genomics data",
        journal: "Nature Biotechnology",
        category: 'tool',
        year: '2023',
        image: 'https://readdy.ai/api/search-image?query=SEACells%20algorithm%20for%20inferring%20transcriptional%20and%20epigenomic%20cellular%20states%20from%20single-cell%20genomics%20data%2C%20scientific%20visualization%20of%20metacell%20construction%20and%20cellular%20state%20inference%2C%20computational%20biology%20workflow%20with%20colorful%20cell%20state%20identification%20and%20epigenomic%20analysis%2C%20single-cell%20data%20aggregation%20with%20transcriptional%20state%20mapping%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=seacells-metacells-v1&orientation=landscape',
        link: 'https://www.nature.com/articles/s41587-023-01716-9',
        analysisMethod: 'metacells',
        toolLabel: 'SEACells'        // ✅ 包名
      },
      {
        title: "Metacells untangle large and complex single-cell transcriptome networks",
        journal: "BMC Bioinformatics",
        category: 'tool',
        year: '2022',
        image: 'https://readdy.ai/api/search-image?query=Metacells%20algorithm%20for%20untangling%20large%20and%20complex%20single-cell%20transcriptome%20networks%2C%20scientific%20visualization%20of%20metacell%20construction%20and%20network%20simplification%2C%20computational%20biology%20workflow%20with%20colorful%20single-cell%20network%20analysis%20and%20data%20aggregation%2C%20complex%20transcriptome%20network%20visualization%20with%20metacell%20identification%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=metacells-network-v1&orientation=landscape',
        link: 'https://bmcbioinformatics.biomedcentral.com/articles/10.1186/s12859-022-04861-1',
        analysisMethod: 'metacells',
        toolLabel: 'Metacells'        // ✅ 包名
      },
      {
        title: "Metacell-2: a divide-and-conquer metacell algorithm for scalable scRNA-seq analysis",
        journal: "Genome Biology",
        category: 'tool',
        year: '2022',
        image: 'https://readdy.ai/api/search-image?query=Metacell-2%20divide-and-conquer%20algorithm%20for%20scalable%20single-cell%20RNA%20sequencing%20analysis%2C%20scientific%20visualization%20of%20metacell%20construction%20with%20divide-and-conquer%20strategy%2C%20computational%20biology%20workflow%20with%20colorful%20hierarchical%20metacell%20identification%20and%20scalable%20data%20processing%2C%20large-scale%20scRNA-seq%20analysis%20framework%20with%20efficient%20metacell%20algorithms%2C%20clean%20scientific%20background%20without%20any%20text%20or%20labels&width=400&height=300&seq=metacell2-divide-conquer-v1&orientation=landscape',
        link: 'https://genomebiology.biomedcentral.com/articles/10.1186/s13059-022-02667-1',
        analysisMethod: 'metacells',
        toolLabel: 'Metacell-2'        // ✅ 包名
      },
      {
        title: "SPOTlight: seeded NMF regression to deconvolute spatial transcriptomics spots with single-cell transcriptomes",
        journal: "Nucleic Acids Research",
        category: "tool",
        year: "2021",
        image: "/images/research/spotlight.png",
        link: "https://academic.oup.com/nar/article/49/9/e50/6129341",
        analysisMethod: "spatial-transcriptomics",
        toolLabel: "SPOTlight"        // ✅ 包名
      },
      {
        "title": "Cell2location maps fine-grained cell types in spatial transcriptomics",
        "journal": "Nature Biotechnology",
        "category": "tool",
        "year": "2022",
        "image": "/images/research/cell2location.png",
        "link": "https://www.nature.com/articles/s41587-021-01139-4",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "Cell2location"        // ✅ 包名
      }, {
        "title": "Deep learning and alignment of spatially resolved single-cell transcriptomes with Tangram",
        "journal": "Nature Methods",
        "category": "tool",
        "year": "2021",
        "image": "/images/research/tangram.png",
        "link": "https://www.nature.com/articles/s41592-021-01264-7",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "Tangram"        // ✅ 包名
      },
      {
        "title": "METASPACE-ML: Context-specific metabolite annotation for imaging mass spectrometry using machine learning",
        "journal": "Nature Communications",
        "category": "tool",
        "year": "2024",
        "image": "/images/research/METASPACE-ML.png",
        "link": "https://www.nature.com/articles/s41467-024-52213-9",
        "analysisMethod": "metabolomics",
        "toolLabel": "METASPACE-ML"
      }, {
        "title": "FDR-controlled metabolite annotation for high-resolution imaging mass spectrometry",
        "journal": "Nature Methods",
        "category": "tool",
        "year": "2017",
        "image": "/images/research/pySM.png",
        "link": "https://www.nature.com/articles/nmeth.4072",
        "analysisMethod": "metabolomics",
        "toolLabel": "pySM"
      }, {
        "title": "ENACT: End-to-End Analysis of Visium High Definition (HD) Data",
        "journal": "Bioinformatics",
        "category": "tool",
        "year": "2025",
        "image": "/images/research/enact.jpeg",
        "link": "https://academic.oup.com/bioinformatics/article/41/3/btaf094/8063614",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "ENACT"
      }, {
        "title": "Bin2cell reconstructs cells from high resolution Visium HD data",
        "journal": "Bioinformatics",
        "category": "tool",
        "year": "2024",
        "image": "/images/research/bin2cell.jpg",
        "link": "https://academic.oup.com/bioinformatics/article/40/9/btae546/7754061",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "Bin2cell"
      }, {
        "title": "Cardinal: an R package for statistical analysis of mass spectrometry-based imaging experiments",
        "journal": "Bioinformatics",
        "category": "tool",
        "year": "2015",
        "image": "/images/research/cardinal.jpeg",
        "link": "https://academic.oup.com/bioinformatics/article/31/14/2418/255407?login=false",
        "analysisMethod": "metabolomics",
        "toolLabel": "Cardinal"
      }, {
        "title": "Screening cell–cell communication in spatial transcriptomics via collective optimal transport",
        "journal": "Nature Methods",
        "category": "tool",
        "year": "2023",
        "image": "/images/research/commot.jpg",
        "link": "https://pmc.ncbi.nlm.nih.gov/articles/PMC9911355/",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "COMMOT"
      },
      {
        "title": "CellMarker 2.0: an updated database of manually curated cell markers in human/mouse and web tools based on scRNA-seq data",
        "journal": "Nucleic Acids Research",
        "category": "tool",
        "year": "2023",
        "image": "/images/research/cellmarker.jpeg",
        "link": "https://academic.oup.com/nar/article/51/D1/D870/6775381",
        "analysisMethod": "cell-annotation",
        "toolLabel": "CellMarker 2.0"
      }, {
        "title": "Inferring histology-associated gene expression gradients in spatial transcriptomic studies",
        "journal": "Nature Communications",
        "category": "tool",
        "year": "2024",
        "image": "/images/research/spata.webp",
        "link": "https://www.nature.com/articles/s41467-024-50904-x",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "SPATA2"
      },
      {
        "title": "Mime: A flexible machine-learning framework to construct and visualize models for clinical characteristics prediction and feature selection",
        "journal": "Computational and Structural Biotechnology Journal",
        "category": "tool",
        "year": "2024",
        "image": "/images/research/mime.jpg",
        "link": "https://pmc.ncbi.nlm.nih.gov/articles/PMC11269309/",
        "analysisMethod": "ML",
        "toolLabel": "Mime"
      }, {
        "title": "Delineating copy number and clonal substructure in human tumors from single-cell transcriptomes",
        "journal": "Nature Biotechnology",
        "category": "tool",
        "year": "2021",
        "image": "/images/research/copycat.jpeg",
        "link": "https://pmc.ncbi.nlm.nih.gov/articles/PMC8122019/",
        "analysisMethod": "cancer",
        "toolLabel": "CopyKAT"
      },
      {
        "title": "Single-cell RNA-seq highlights intratumoral heterogeneity in primary glioblastoma",
        "journal": "Science",
        "category": "tool",
        "year": "2014",
        "image": "/images/research/infercnv.jpeg",
        "link": "https://pmc.ncbi.nlm.nih.gov/articles/PMC4123637/",
        "analysisMethod": "cancer",
        "toolLabel": "inferCNV"
      }, {
        "title": "High-resolution alignment of single-cell and spatial transcriptomes with CytoSPACE",
        "journal": "Nature Biotechnology",
        "category": "tool",
        "year": "2023",
        "image": "/images/research/cytospace.png",
        "link": "https://www.nature.com/articles/s41587-023-01697-9",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "CytoSPACE"
      }, {
        "title": "Benchmarking scRNA-seq copy number variation callers",
        "journal": "Nature Communications",
        "category": "tool",
        "year": "2025",
        "image": "/images/research/cancer_benchmark.webp",
        "link": "https://www.nature.com/articles/s41467-025-62359-9#Fig1",
        "analysisMethod": "cancer",
        "toolLabel": "benchmark_CNV"
      }, {
        "title": "Haplotype-aware analysis of somatic copy number variations from single-cell transcriptomes",
        "journal": "Nature Biotechnology",
        "category": "tool",
        "year": "2023",
        "image": "/images/research/Numbat.png",
        "link": "https://www.nature.com/articles/s41587-022-01468-y",
        "analysisMethod": "cancer",
        "toolLabel": "Numbat"
      }, {
        "title": "scRank infers drug-responsive cell types from untreated scRNA-seq data using a target-perturbed gene regulatory network",
        "journal": "Cell Reports Medicine",
        "category": "tool",
        "year": "2024",
        "image": "/images/research/scrank.jpg",
        "link": "https://www.cell.com/cell-reports-medicine/fulltext/S2666-3791(24)00260-X",
        "analysisMethod": 'cell-perturbation',
        "toolLabel": "scRank"
      }, {
        "title": "Inferring gene regulatory networks from single-cell multiome data using atlas-scale external data",
        "journal": "Nature Biotechnology",
        "category": "tool",
        "year": "2025",
        "image": "/images/research/linger.png",
        "link": "https://www.nature.com/articles/s41587-024-02182-7",
        "analysisMethod": "tf-prediction",
        "toolLabel": "LINGER"
      },
      {
        "title": "AMULET: a novel read count-based method for effective multiplet detection from single nucleus ATAC-seq data",
        "journal": "Genome Biology",
        "category": "tool",
        "year": "2021",
        "image": "/images/research/amulet.png",
        "link": "https://genomebiology.biomedcentral.com/articles/10.1186/s13059-021-02469-x",
        "analysisMethod": "quality-control",
        "toolLabel": "AMULET"
      }, {
        "title": "Inferring allele-specific copy number aberrations and tumor phylogeography from spatially resolved transcriptomics",
        "journal": "Nature Methods",
        "category": "tool",
        "year": "2024",
        "image": "/images/research/CalisoST.webp",
        "link": "https://www.nature.com/articles/s41592-024-02438-9",
        "analysisMethod": "cancer",
        "toolLabel": "CalicoST"
      }, {
        "title": "STORIES: learning cell fate landscapes from spatial transcriptomics using optimal transport",
        "journal": "Nature Methods",
        "category": "tool",
        "year": "2025",
        "image": "/images/research/stories.webp",
        "link": "https://www.nature.com/articles/s41592-025-02855-4",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "STORIES"
      }, {
        "title": "Optimizing Xenium In Situ data utility by quality assessment and best-practice analysis workflows",
        "journal": "Nature Methods",
        "category": "tool",
        "year": "2025",
        "image": "/images/research/Xenium_benchmark.png",
        "link": "https://www.nature.com/articles/s41592-025-02797-0",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "Xenium_benchmarking"
      }, {
        "title": "Hotspot identifies informative gene modules across modalities of single-cell genomics",
        "journal": "Cell Systems",
        "category": "tool",
        "year": "2021",
        "image": "/images/research/hotspot.jpg",
        "link": "https://www.cell.com/cell-systems/fulltext/S2405-4712(21)00114-9?_returnURL=https%3A%2F%2Flinkinghub.elsevier.com%2Fretrieve%2Fpii%2FS2405471221001149%3Fshowall%3Dtrue",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "Hotspot"
      }, {
        "title": "Robust decomposition of cell type mixtures in spatial transcriptomics",
        "journal": "Nature Biotechnology",
        "category": "tool",
        "year": "2022",
        "image": "/images/research/rctd.png",
        "link": "https://www.nature.com/articles/s41587-021-00830-w",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "RCTD"
      }, {
        "title": "Cell type-specific inference of differential expression in spatial transcriptomics",
        "journal": "Nature Methods",
        "category": "tool",
        "year": "2022",
        "image": "/images/research/spacexr.png",
        "link": "https://www.nature.com/articles/s41592-022-01561-2",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "spacexr"
      }, {
        "title": "Squidiff: predicting cellular development and responses to perturbations using a diffusion model",
        "journal": "Nature Methods",
        "category": "tool",
        "year": "2025",
        "image": "/images/research/squidiff.png",
        "link": "https://www.nature.com/articles/s41592-025-02877-y",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "Squidiff"
      }, {
        "title": "Cell segmentation in imaging-based spatial transcriptomics",
        "journal": "Nature Biotechnology",
        "category": "tool",
        "year": "2022",
        "image": "/images/research/baysor.png",
        "link": "https://www.nature.com/articles/s41587-021-01044-w",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "Baysor"
      }, {
        "title": "SoupX removes ambient RNA contamination from droplet-based single-cell RNA sequencing data",
        "journal": "GigaScience",
        "category": "tool",
        "year": "2020",
        "image": "/images/research/soupx.png",
        "link": "https://academic.oup.com/gigascience/article/9/12/giaa151/6049831?login=false",
        "analysisMethod": "quality-control",
        "toolLabel": "SoupX"
      }, {
        "title": "TACCO unifies annotation transfer and decomposition of cell identities for single-cell and spatial omics",
        "journal": "Nature Biotechnology",
        "category": "tool",
        "year": "2023",
        "image": "/images/research/tacco.png",
        "link": "https://www.nature.com/articles/s41587-023-01657-3",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "TACCO"
      }, {
        "title": "Topological velocity inference from spatial transcriptomic data",
        "journal": "Nature Biotechnology",
        "category": "tool",
        "year": "2025",
        "image": "/images/research/topovelo.png",
        "link": "https://www.nature.com/articles/s41587-025-02688-8",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "TopoVelo"
      }, {
        "title": "hdWGCNA identifies co-expression networks in high-dimensional transcriptomics data",
        "journal": "Cell Reports Methods",
        "category": "tool",
        "year": "2023",
        "image": "/images/research/hdwgcna.jpg",
        "link": "https://www.sciencedirect.com/science/article/pii/S2667237523001273",
        "analysisMethod": "other",
        "toolLabel": "hdWGCNA"
      }, {
        "title": "Inferring super-resolution tissue architecture by integrating spatial transcriptomics with histology",
        "journal": "Nature Biotechnology",
        "category": "tool",
        "year": "2024",
        "image": "/images/research/istar.png",
        "link": "https://www.nature.com/articles/s41587-023-02019-9",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "iStar"
      }, {
        "title": "Mapping Transcriptomic Vector Fields of Single Cells",
        "journal": "Cell",
        "category": "tool",
        "year": "2022",
        "image": "/images/research/dynamo.jpg",
        "link": "https://www.sciencedirect.com/science/article/pii/S0092867421015774",
        "analysisMethod": "trajectory",
        "toolLabel": "Dynamo"
      }, {
        "title": "OmicVerse: a framework for bridging and deepening insights across bulk and single-cell sequencing",
        "journal": "Nature Communications",
        "category": "tool",
        "year": "2024",
        "image": "/images/research/omicverse.png",
        "link": "https://www.nature.com/articles/s41467-024-49638-8",
        "analysisMethod": "other",
        "toolLabel": "OmicVerse"
      }, {
        "title": "Mitigating inconsistencies in GWAS follow-up analyses with LocusCompare2",
        "journal": "Nature Genetics",
        "category": "tool",
        "year": "2025",
        "image": "/images/research/LocusCompare2.webp",
        "link": "https://www.nature.com/articles/s41588-025-02331-x",
        "analysisMethod": "geneomics",
        "toolLabel": "LocusCompare2"
      }, {
        "title": "Cell type and gene expression deconvolution with BayesPrism enables Bayesian integrative analysis across bulk and single-cell RNA sequencing in oncology",
        "journal": "Nature Cancer",
        "category": "tool",
        "year": "2022",
        "image": "/images/research/bayesprism.png",
        "link": "https://www.nature.com/articles/s43018-022-00356-3",
        "analysisMethod": "cancer",
        "toolLabel": "BayesPrism"
      }, {
        "title": "sccomp: Robust differential composition and variability analysis for single-cell data",
        "journal": "PNAS",
        "category": "tool",
        "year": "2023",
        "image": "/images/research/2203828120.jpg",
        "link": "https://www.pnas.org/doi/10.1073/pnas.2203828120",
        "analysisMethod": "cell-abundance",
        "toolLabel": "sccomp"
      }, {
        "title": "Benchmarking algorithms for spatially variable gene identification in spatial transcriptomics",
        "journal": "Bioinformatics",
        "category": "tool",
        "year": "2025",
        "image": "/images/research/benchmark_svg1.jpeg",
        "link": "https://academic.oup.com/bioinformatics/article/41/4/btaf131/8096371",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "benchmark_svg"
      }, {
        "title": "Categorization of 34 computational methods to detect spatially variable genes from spatially resolved transcriptomics data",
        "journal": "Nature Communications",
        "category": "tool",
        "year": "2025",
        "image": "/images/research/benchmark_svg2.webp",
        "link": "https://www.nature.com/articles/s41467-025-56080-w#Sec13",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "benchmark_svg"
      }, {
        "title": "Benchmarking computational methods for detecting spatial domains and domain-specific spatially variable genes from spatial transcriptomics data",
        "journal": "Nucleic Acids Res",
        "category": "tool",
        "year": "2025",
        "image": "/images/research/benchmark_svg3.jpg",
        "link": "https://pmc.ncbi.nlm.nih.gov/articles/PMC12000868/",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "benchmark_svg"
      }, {
        "title": "STalign: Alignment of spatial transcriptomics data using diffeomorphic metric mapping",
        "journal": "Nature Communications",
        "category": "tool",
        "year": "2023",
        "image": "/images/research/43915.webp",
        "link": "https://www.nature.com/articles/s41467-023-43915-7",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "STalign"
      },{
        "title": "Interpretable spatially aware dimension reduction of spatial transcriptomics with STAMP",
        "journal": "Nature Methods",
        "category": "tool",
        "year": "2024",
        "image": "/images/research/030015_956.png",
        "link": "https://www.nature.com/articles/s41592-024-02463-8",
        "analysisMethod": "spatial-transcriptomics",
        "toolLabel": "STAMP"
      },
    ]
  };

  const examplesForDirection = journalExamples[selectedDirection] || [];

  const normalizedToolSearch = toolSearch.trim().toLowerCase();

  const filteredExamples =
    selectedDirection === 'analysis-tools'
      ? examplesForDirection
        // 1）先按 analysisMethod 分类
        .filter(example =>
          selectedCategory === 'all'
            ? true
            : example.analysisMethod === selectedCategory
        )
        // 2）再按 toolLabel 搜索（空字符串则不过滤）
        .filter(example => {
          if (!normalizedToolSearch) return true;
          const label = example.toolLabel?.toLowerCase() || '';
          return label.includes(normalizedToolSearch);
        })
      : examplesForDirection.filter(example =>
        selectedCategory === 'all'
          ? true
          : example.category === selectedCategory
      );

  // 分页逻辑
  const totalPages = Math.ceil(filteredExamples.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredExamples.slice(startIndex, endIndex);

  // 重置分页当筛选条件改变时
  const handleDirectionChange = (directionId: string) => {
    setSelectedDirection(directionId);
    setSelectedCategory('all');
    setCurrentPage(1);
  };

  const handleCategoryChange = (categoryId: any) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // 滚动到顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-green-100 text-green-700';
      case 'tool': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getCategoryText = (category: string, impactFactor?: number) => {
    if (selectedDirection === 'analysis-tools') {
      return '分析工具';
    }
    switch (category) {
      case 'high': return 'IF ≥ 10';
      case 'medium': return 'IF 5-8';
      case 'low': return 'IF ≤ 3';
      default: return '';
    }
  };

  const getAnalysisMethodText = (method?: string) => {
    switch (method) {
      case 'trajectory': return '细胞轨迹';
      case 'tf-prediction': return '转录因子';
      case 'multiomics-integration': return '多组学整合';
      case 'metabolomics': return '代谢组学';
      case 'geneomics': return '基因组学';
      case 'cell-abundance': return '细胞丰度';
      case 'cell-communication': return '细胞通讯';
      case 'quality-control': return '质控';
      case 'data-integration': return '数据整合';
      case 'cell-perturbation': return '细胞扰动';
      case 'cell-annotation': return '细胞注释';
      case 'spatial-transcriptomics': return '空间转录组';
      case 'metacells': return 'Metacells';
      case 'ML': return '机器学习';
      case 'cancer': return '癌症';
      case 'other': return '其它';
      default: return '分析工具';
    }
  };

  return (
    <section className="py-20 bg-gray-50">
      {/* {debugBadge} */}
      <div className="max-w-7xl mx-auto px-6">
        {/* Direction Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {directions.map((direction) => (
            <button
              key={direction.id}
              onClick={() => handleDirectionChange(direction.id)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${selectedDirection === direction.id
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
                }`}
            >
              <div className="w-5 h-5 flex items-center justify-center mr-2 inline-block">
                <i className={`${direction.icon} text-lg`}></i>
              </div>
              {direction.name}
            </button>
          ))}
        </div>

        {/* Category Filter */}
        <div className="flex flex-col items-center gap-4 mb-12">
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${selectedCategory === category.id
                  ? category.color + ' ring-2 ring-offset-2 ring-blue-500'
                  : category.color + ' hover:ring-2 hover:ring-offset-2 hover:ring-blue-300'
                  }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* 仅在“分析工具”方向下显示搜索框，按 toolLabel 搜索 */}
          {selectedDirection === 'analysis-tools' && (
            <div className="w-full max-w-md">
              <div className="relative">
                <input
                  type="text"
                  value={toolSearch}
                  onChange={(e) => {
                    setToolSearch(e.target.value);
                    setCurrentPage(1); // 搜索时回到第一页
                  }}
                  placeholder="按工具名搜索，如 Seurat、scVI、CellChat..."
                  className="w-full rounded-full border border-gray-200 px-4 py-2 pl-10 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <i className="ri-search-line text-base" />
                </span>
              </div>
            </div>
          )}
        </div>


        {/* 结果统计 */}
        {filteredExamples.length > 0 && (
          <div className="text-center mb-8">
            <p className="text-gray-600">
              共找到 <span className="font-semibold text-blue-600">{filteredExamples.length}</span> 篇文献
              {totalPages > 1 && (
                <span className="ml-2">
                  第 <span className="font-semibold">{currentPage}</span> 页，共 <span className="font-semibold">{totalPages}</span> 页
                </span>
              )}
            </p>
          </div>
        )}

        {/* Journal Examples */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {currentItems.map((example, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer"
              onClick={() => window.open(example.link, '_blank')}
            >
              <div className="relative h-48">
                <img
                  src={example.image}
                  alt={example.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

                {/* 外层卡片记得是 relative */}
                <div className="relative">
                  {/* 这里是图片之类的内容 */}
                  {/* <img ... /> */}

                  {/* ✅ 底部：主标签 + tags，都挂在这一层 absolute 上 */}
                  <div className="absolute bottom-4 left-4 right-4 flex flex-col-reverse items-start gap-2">
                    {/* 1️⃣ tags：放在前面，因为 flex-col-reverse -> 它会出现在最底部 */}
                    {example.keywords && example.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {example.keywords.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-black/45 text-white border border-white/20 backdrop-blur-sm"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* 2️⃣ 主标签条：在 tags 上面；没有 tags 时自己贴到底 */}
                    <div className="flex w-full items-center justify-between gap-2">
                      {/* 主标签：IF 或 方法类型 */}
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getCategoryBadge(
                          example.category
                        )}`}
                      >
                        {selectedDirection === 'analysis-tools'
                          ? getAnalysisMethodText(example.analysisMethod)
                          : getCategoryText(example.category, example.impactFactor)}
                      </span>

                      {/* 右侧：仅对 category === 'tool' 的文献显示工具名标签 */}
                      {selectedDirection === 'analysis-tools' &&
                        example.category === 'tool' &&
                        example.toolLabel && (
                          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-white/90 text-gray-900 shadow">
                            {example.toolLabel}
                          </span>
                        )}
                    </div>
                  </div>
                </div>


              </div>

              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 leading-tight">
                  {example.title}
                </h3>

                <div className="flex items-center justify-between text-sm mb-2">
                  <div className="flex items-center">
                    <div className="w-4 h-4 flex items-center justify-center mr-2">
                      <i className="ri-book-line text-blue-600"></i>
                    </div>
                    <span className="font-medium text-blue-600">{example.journal}</span>
                  </div>
                  <span className="text-gray-500">{example.year}</span>
                </div>
              </div>
            </div>
          ))}
        </div>


        {/* 分页组件 */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-12 space-x-2">
            {/* 上一页 */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
                }`}
            >
              <i className="ri-arrow-left-line mr-1"></i>
              上一页
            </button>

            {/* 页码 */}
            <div className="flex space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // 显示逻辑：始终显示第1页和最后一页，当前页及其前后各1页
                const showPage =
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1);

                const showEllipsis =
                  (page === 2 && currentPage > 4) ||
                  (page === totalPages - 1 && currentPage < totalPages - 3);

                if (!showPage && !showEllipsis) return null;

                if (showEllipsis) {
                  return (
                    <span key={`ellipsis-${page}`} className="px-3 py-2 text-gray-400">
                      ...
                    </span>
                  );
                }

                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 rounded-lg font-medium transition-all duration-200 cursor-pointer ${currentPage === page
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
                      }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            {/* 下一页 */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${currentPage === totalPages
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
                }`}
            >
              下一页
              <i className="ri-arrow-right-line ml-1"></i>
            </button>
          </div>
        )}

        {filteredExamples.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
              <i className="ri-search-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-gray-500">该分类下暂无文献，请选择其他分类查看</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ResearchDirections;
